import React, {CSSProperties, useMemo} from 'react';
import cx from 'classnames';
import {ThumbnailProps} from './MediaGalleryThumbnailFactory';
import ThumbnailBase from './ThumbnailBase';
import {calcRotation, flipSize} from '../../../Utils/Asset';
import {SelectionMode} from '../MediaGallery';
import {ThemeName, getTheme} from '@taopix/taopix-design-system';
import './ThumbnailProportional.scss';

const ThumbnailProportional = ({
  image,
  openDetailsDialog,
  used,
  maxSelectionReached,
  onSelectPicture,
  selectableImages,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onTouchEnd,
  onDeleteAsset,
  selected,
  localeString,
  currencyCode,
  expired,
  showDeleteButton = false,
  showPricing,
  showUsage,
  showLabel,
  selectionAppearance,
  disabled,
  overlayDisplayMode,
  selectionMode = SelectionMode.Multi,
  onKeyDown,
}: ThumbnailProps) => {
  const zeroSize = image.width === 0 || image.height === 0 ? true : false;

  // if the image is not processed, ignore rotation
  const rotation = useMemo(() => {
    return calcRotation(image);
  }, [image]);

  const rotatedStyles = useMemo(() => {
    if (rotation === 0) {
      return {};
    }
    if (180 === rotation) {
      return {
        position: 'absolute',
        transform: 'rotate(180deg)',
      };
    }
    const translate = rotation === 270 ? 'translateX' : 'translateY';
    return {
      position: 'absolute',
      transform: `rotate(${rotation}deg) scale(calc(var(--asset-height)/var(--asset-width))) ${translate}(-100%)`,
      transformOrigin: 'top left',
    };
  }, [rotation]) as CSSProperties;

  const sizing = flipSize(rotation, {width: image.width, height: image.height});

  const cssDimensions = {
    '--asset-width': sizing.width,
    '--asset-height': sizing.height,
    '--asset-ratio': sizing.width / sizing.height,
    '--thumbnail-max': 'calc(var(--target-thumbnail-height)*1.8px)', // The maximum size prevents the last or (only) thumbnail in a group from being massive,
  } as React.CSSProperties;

  const containerClasses = [
    'thumbnailContainer',
    'group/thumbnail',
    zeroSize ? 'thumbnailContainer--square' : 'thumbnailContainer--proportional',
    getTheme(ThemeName.Container, true, false, false),
  ];

  const imageClasses = ['thumbnailImage', zeroSize && 'thumbnailImage--square'];

  return (
    <li className={cx(containerClasses)} style={cssDimensions}>
      <ThumbnailBase
        currencyCode={currencyCode}
        imageClasses={cx(imageClasses)}
        imageCSS={rotatedStyles}
        disabled={disabled}
        expired={expired}
        image={image}
        localeString={localeString}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        onSelectPicture={onSelectPicture}
        onTouchEnd={onTouchEnd}
        onTouchStart={onTouchStart}
        openDetailsDialog={openDetailsDialog}
        selectionAppearance={selectionAppearance}
        selectableImages={selectableImages}
        selected={selected}
        showDeleteButton={showDeleteButton}
        showPricing={showPricing}
        showUsage={showUsage}
        showLabel={showLabel}
        used={used}
        overlayDisplayMode={overlayDisplayMode}
        onDeleteAsset={onDeleteAsset}
        maxSelectionReached={maxSelectionReached}
        selectionMode={selectionMode}
        onKeyDown={onKeyDown}
      />
    </li>
  );
};

export default ThumbnailProportional;
